import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";

import {
  loginWithGoogle,
  requestEmailToken,
  verifyEmailToken,
} from "@/lib/api/auth";
import { getServices } from "@/lib/api/services";
import { getUser } from "@/lib/api/user";
import { markDevLoggedOut } from "@/lib/auth/token-provider";
import { resetAnalytics } from "@/lib/posthog";
import { storage } from "@/lib/storage";
import { USER_QUERY_KEY } from "@/hooks/use-user";
import { useAuthStore } from "@/stores/auth-store";
import { useServiceStore } from "@/stores/service-store";

function clearAllCookies() {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }
}

export const useRequestEmailTokenMutation = () =>
  useMutation({
    mutationFn: (data: { email: string }) =>
      requestEmailToken(data).then((res) => res.data),
  });

export const useVerifyEmailTokenMutation = () =>
  useMutation({
    mutationFn: (data: { token: string }) =>
      verifyEmailToken(data).then((res) => res.data),
  });

export const useGoogleLoginMutation = () =>
  useMutation({
    mutationFn: (data: { credential: string }) =>
      loginWithGoogle(data).then((res) => res.data),
  });

// Shared by every auth flow. It takes only the token because identity comes
// from GET /dashboard/user; the magic-link flow has no email at hand.
export function usePostLoginRedirect() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToken, clearSession } = useAuthStore();
  const { setSelectedService } = useServiceStore();

  return useCallback(
    async (token: string) => {
      // Persist the JWT first so the requests below carry it.
      setToken(token);

      try {
        const [userRes, services] = await Promise.all([
          getUser(),
          getServices(),
        ]);
        const user = userRes.data;

        // Warm the caches so the dashboard doesn't refetch these on landing.
        queryClient.setQueryData(USER_QUERY_KEY, user);
        queryClient.setQueryData(["services"], services);

        if (!user.settings?.onboarding_service || services.length === 0) {
          navigate("/onboarding", { replace: true });
          return;
        }

        setSelectedService(services[0]);
        queryClient.setQueryData(["service", services[0].id], services[0]);
        navigate("/", { replace: true });
      } catch (error) {
        // Only a 401 means the just-issued token was rejected, so only then
        // clear it. A 402 (the interceptor already routed to /pricing; the user
        // is authenticated, just unpaid) or a transient 5xx/network blip leaves
        // a valid token in place so the user can retry without a new magic link.
        if (isAxiosError(error) && error.response?.status === 401) {
          clearSession();
        }
        throw error;
      }
    },
    [navigate, queryClient, setSelectedService, setToken, clearSession]
  );
}

export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clearSession } = useAuthStore();

  return useCallback(() => {
    // Preserve the "last used" login hint across sign-out (the returning user
    // needs it on /login) even as everything else is wiped.
    const lastLoginMethod = storage.getLastLoginMethod();
    clearSession();
    queryClient.clear();
    resetAnalytics();
    localStorage.clear();
    sessionStorage.clear();
    clearAllCookies();
    if (lastLoginMethod) storage.setLastLoginMethod(lastLoginMethod);
    // Dev-only: don't let the startup VITE_DEV_AUTH_JWT seed sign us straight
    // back in on the next reload (no-op in production).
    markDevLoggedOut();
    navigate("/logout-success", { replace: true });
  }, [clearSession, navigate, queryClient]);
}
